import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";

function Login() {
  const [phone, setPhone] = useState("+91");
  const [otp, setOtp] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [step, setStep] = useState("phone"); // "phone" or "otp"
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Get backend URL from environment or default
  const BACKEND_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";

  // Normalize phone numbers before sending to backend
  const normalizePhoneInput = (p) => {
    if (!p) return "";
    let v = p.trim();
    // remove non-digit characters for analysis
    const digits = v.replace(/\D/g, "");
    if (v.startsWith("+")) return v;
    if (digits.length === 10) return "+91" + digits;
    if (v.startsWith("0")) return "+91" + v.replace(/^0+/, "");
    // fallback: prefix +91 if no plus provided
    return "+91" + digits;
  };

  const handleSendOTP = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    // Basic validation using digit count
    const rawDigits = phone.replace(/\D/g, "");
    if (!rawDigits || rawDigits.length < 10) {
      setError("Please enter a valid phone number");
      return;
    }

    const normalizedPhone = normalizePhoneInput(phone);

    setLoading(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/send-otp`, {
        phone: normalizedPhone,
      });

      if (response.status === 200) {
        setSuccess("OTP sent successfully! Check your SMS.");
        setStep("otp");
      }
    } catch (err) {
      console.error("OTP send error:", err);
      if (err.response?.status === 403) {
        setError("This phone number is not authorized");
      } else if (err.response?.status === 429) {
        setError("Too many attempts. Please try again later.");
      } else {
        setError(err.response?.data?.message || "Failed to send OTP. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!otp || otp.trim().length < 4) {
      setError("Please enter the OTP");
      return;
    }

    setLoading(true);
    try {
      const normalizedPhone = normalizePhoneInput(phone);
      const response = await axios.post(`${BACKEND_URL}/verify-otp`, {
        phone: normalizedPhone,
        code: otp.trim(),
      });

      if (response.status === 200) {
        setSuccess("Login successful!");
        localStorage.setItem("isLoggedIn", "true");
        localStorage.setItem("userPhone", normalizePhoneInput(phone));
        setTimeout(() => {
          navigate("/add-post");
        }, 1000);
      }
    } catch (err) {
      console.error("OTP verification error:", err);
      if (err.response?.status === 403) {
        setError("This phone number is not authorized");
      } else if (err.response?.status === 400) {
        setError("Invalid OTP. Please try again.");
      } else if (err.response?.status === 429) {
        setError("Too many attempts. Please try again later.");
      } else {
        setError(err.response?.data?.message || "Verification failed. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setStep("phone");
    setPhone("");
    setOtp("");
    setError("");
    setSuccess("");
  };

  return (
    <>
      <div
        style={{
          maxWidth: 400,
          margin: "60px auto",
          padding: 24,
          background: "#fff",
          borderRadius: 8,
          boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
        }}
      >
        <h2 style={{ textAlign: "center", marginBottom: 24, color: "#333" }}>
          {step === "phone" ? "Enter Phone Number" : "Enter OTP"}
        </h2>

        {step === "phone" ? (
          <form onSubmit={handleSendOTP}>
            <label style={{ display: "block", marginBottom: 8, fontWeight: 500 }}>
              Phone Number
            </label>
            <input
              type="tel"
              placeholder="+1234567890"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              style={{
                width: "100%",
                padding: 10,
                marginBottom: 16,
                borderRadius: 4,
                border: "1px solid #ccc",
                fontSize: 14,
                boxSizing: "border-box",
              }}
              disabled={loading}
              required
            />
            {error && (
              <div
                style={{
                  color: "#d32f2f",
                  marginBottom: 12,
                  fontSize: 14,
                  padding: 8,
                  background: "#ffebee",
                  borderRadius: 4,
                }}
              >
                {error}
              </div>
            )}
            {success && (
              <div
                style={{
                  color: "#388e3c",
                  marginBottom: 12,
                  fontSize: 14,
                  padding: 8,
                  background: "#e8f5e9",
                  borderRadius: 4,
                }}
              >
                {success}
              </div>
            )}
            <button
              type="submit"
              style={{
                width: "100%",
                padding: 10,
                background: loading ? "#ccc" : "#1976d2",
                color: "#fff",
                border: "none",
                borderRadius: 4,
                fontWeight: 600,
                cursor: loading ? "not-allowed" : "pointer",
              }}
              disabled={loading}
            >
              {loading ? "Sending..." : "Send OTP"}
            </button>
          </form>
        ) : (
          <form onSubmit={handleVerifyOTP}>
            <label style={{ display: "block", marginBottom: 8, fontWeight: 500 }}>
              OTP Code
            </label>
            <p style={{ fontSize: 12, color: "#666", marginBottom: 12 }}>
              Sent to: {normalizePhoneInput(phone)}
            </p>
            <input
              type="text"
              placeholder="000000"
              value={otp}
              onChange={(e) => setOtp(e.target.value)}
              style={{
                width: "100%",
                padding: 10,
                marginBottom: 16,
                borderRadius: 4,
                border: "1px solid #ccc",
                fontSize: 18,
                letterSpacing: 2,
                boxSizing: "border-box",
              }}
              disabled={loading}
              required
            />
            {error && (
              <div
                style={{
                  color: "#d32f2f",
                  marginBottom: 12,
                  fontSize: 14,
                  padding: 8,
                  background: "#ffebee",
                  borderRadius: 4,
                }}
              >
                {error}
              </div>
            )}
            {success && (
              <div
                style={{
                  color: "#388e3c",
                  marginBottom: 12,
                  fontSize: 14,
                  padding: 8,
                  background: "#e8f5e9",
                  borderRadius: 4,
                }}
              >
                {success}
              </div>
            )}
            <button
              type="submit"
              style={{
                width: "100%",
                padding: 10,
                background: loading ? "#ccc" : "#1976d2",
                color: "#fff",
                border: "none",
                borderRadius: 4,
                fontWeight: 600,
                marginBottom: 8,
                cursor: loading ? "not-allowed" : "pointer",
              }}
              disabled={loading}
            >
              {loading ? "Verifying..." : "Verify OTP"}
            </button>
            <button
              type="button"
              onClick={handleReset}
              style={{
                width: "100%",
                padding: 10,
                background: "#f5f5f5",
                color: "#666",
                border: "1px solid #ddd",
                borderRadius: 4,
                fontWeight: 600,
                cursor: "pointer",
              }}
              disabled={loading}
            >
              Use Different Number
            </button>
          </form>
        )}
      </div>
    </>
  );
}

export default Login;

